/**
 * UsersPage — list of frontline users for the currently selected org.
 *
 * Matches the May 2026 Figma:
 *   • Toolbar: search icon, "Users" title + N TOTAL / UP TO DATE / INACTIVE
 *     summary, overflow, split "+ Add user" (Add user / Import from CSV),
 *     view-toggle.
 *   • Table columns: checkbox, USER NAME (sort), EMAIL, EMPLOYEE ID, ROLE,
 *     TEAMS (pill), SITES ("N sites" link or city), ASSIGNED DEVICE,
 *     STATUS (Active/Inactive dot pill), per-row pen / trash.
 *
 * Backed by /api/orgs/{orgId}/users (services/userService.js).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import userService from '../services/userService';
import UserDrawer from '../components/users/UserDrawer';
import CsvImportModal from '../components/users/CsvImportModal';
const PAGE_SIZE = 50;
function fullName(u) {
  const first = u.preferredFirstName || u.firstName || '';
  const last  = u.preferredLastName  || u.lastName  || '';
  return `${first} ${last}`.trim() || u.customerEmployeeId || '—';
}
function normStatus(s) {
  return String(s || '').toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE';
}
function renderSites(u) {
  const list = (u.siteNames || u.primarySiteName || '').toString().trim();
  const count = u.siteCount ?? (list ? list.split(',').filter(Boolean).length : 0);
  if (!count)      return <span className="up-muted">-</span>;
  if (count === 1) return <span>{list}</span>;
  return <span className="up-sites-link">{count} sites</span>;
}

function rolesText(u) {
  if (Array.isArray(u.roleNames) && u.roleNames.length > 0) {
    return u.roleNames.join(', ');
  }
  return u.roleName || '';
}

export default function UsersPage() {
  const { orgId, siteId } = useTenant();
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort]             = useState({ key: 'name', asc: true });
  const [selected, setSelected]     = useState(() => new Set());
  const [showAddUser, setShowAddUser]   = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [editingUser, setEditingUser]   = useState(null);
  const [deletingId, setDeletingId]     = useState(null);
  const [refreshTick, setRefreshTick]   = useState(0);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef(null);
  const refreshList = () => setRefreshTick((t) => t + 1);
  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const onDoc = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [addMenuOpen]);
  useEffect(() => {
    if (!orgId) { setRows([]); setTotal(0); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    userService.listUsers(orgId, 0, PAGE_SIZE, siteId || undefined)
      .then((res) => {
        if (cancelled) return;
        setRows(res?.content ?? res?.items ?? []);
        setTotal(res?.totalElements ?? (res?.content?.length ?? 0));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message ?? e.message ?? 'Failed to load users');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, siteId, refreshTick]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q
      ? rows
      : rows.filter((u) => [
          fullName(u),
          u.email || '',
          u.customerEmployeeId || '',
          rolesText(u),
          u.siteNames || u.primarySiteName || '',
          u.teamName || '',
          u.assignedDeviceId || '',
          u.status || '',
        ].join(' ').toLowerCase().includes(q));
    const get = (u) => {
      switch (sort.key) {
        case 'name':       return fullName(u);
        case 'email':      return u.email || '';
        case 'employeeId': return u.customerEmployeeId || '';
        case 'role':       return rolesText(u);
        case 'team':       return u.teamName || '';
        case 'site':       return u.siteNames || u.primarySiteName || '';
        case 'device':     return u.assignedDeviceId || '';
        case 'status':     return normStatus(u.status);
        default:           return '';
      }
    };
    return [...base].sort((a, b) =>
      (sort.asc ? 1 : -1) * String(get(a)).localeCompare(String(get(b))),
    );
  }, [rows, search, sort]);
  const counts = useMemo(() => {
    let active = 0, inactive = 0;
    rows.forEach((u) => {
      if (normStatus(u.status) === 'ACTIVE') active += 1; else inactive += 1;
    });
    return { total: rows.length || total, active, inactive };
  }, [rows, total]);
  const allChecked  = filtered.length > 0 && filtered.every((u) => selected.has(u.employeeId));
  const someChecked = !allChecked && filtered.some((u) => selected.has(u.employeeId));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) filtered.forEach((u) => next.delete(u.employeeId));
      else            filtered.forEach((u) => next.add(u.employeeId));
      return next;
    });
  };
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));
  const handleEdit = (u) => setEditingUser(u);
  const handleDelete = async (u) => {
    const name = fullName(u);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    setDeletingId(u.employeeId);
    setError(null);
    try {
      await userService.deleteUser(u.employeeId, orgId);
      refreshList();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };
  const ThSort = ({ id, label }) => (
    <th>
      <button className="data-table-sort" onClick={() => toggleSort(id)}>
        {label}
        <i className={`fas fa-sort${sort.key === id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
      </button>
    </th>
  );
  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search users"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>Users</h1>
            <div className="up-summary">
              <span className="up-summary-item"><strong>{counts.total}</strong> TOTAL</span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-active" /> {counts.active} UP TO DATE
              </span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-inactive" /> {counts.inactive} INACTIVE
              </span>
            </div>
            {searchOpen && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <div className="up-split" ref={addMenuRef}>
            <button
              type="button"
              className="btn-primary-pill up-split-main"
              onClick={() => { setAddMenuOpen(false); setShowAddUser(true); }}
            >
              <i className="fas fa-plus"></i> Add user
            </button>
            <button
              type="button"
              className="btn-primary-pill up-split-caret"
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
              onClick={() => setAddMenuOpen((v) => !v)}
            >
              <i className="fas fa-chevron-down"></i>
            </button>
            {addMenuOpen && (
              <div className="up-split-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="up-split-menu-item"
                  onClick={() => { setAddMenuOpen(false); setShowAddUser(true); }}
                >
                  <i className="fas fa-plus"></i> Add user
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="up-split-menu-item"
                  onClick={() => {
                    setAddMenuOpen(false);
                    setShowCsvImport(true);
                  }}
                >
                  <i className="far fa-file"></i> Import from CSV file
                </button>
              </div>
            )}
          </div>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>
      {error && <div className="users-error">{error}</div>}
      <table className="data-table cm-table sp-table up-table">
        <thead>
          <tr>
            <th className="cm-col-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                onChange={toggleAll}
                aria-label="Select all users"
              />
            </th>
            <ThSort id="name"       label="USER NAME" />
            <ThSort id="email"      label="EMAIL" />
            <ThSort id="employeeId" label="EMPLOYEE ID" />
            <ThSort id="role"       label="ROLE" />
            <th>TEAMS</th>
            <ThSort id="site"       label="SITES" />
            <ThSort id="device"     label="ASSIGNED DEVICE" />
            <ThSort id="status"     label="STATUS" />
            <th className="cm-col-actions" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30 }}>
              <i className="fas fa-spinner fa-spin" /> Loading…
            </td></tr>
          )}
          {!loading && filtered.length === 0 && (
            <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
              No users found.
            </td></tr>
          )}
          {!loading && filtered.map((u) => {
            const status = normStatus(u.status);
            return (
              <tr key={u.employeeId}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(u.employeeId)}
                    onChange={() => toggleRow(u.employeeId)}
                    aria-label={`Select ${fullName(u)}`}
                  />
                </td>
                <td className="up-cell-name">{fullName(u)}</td>
                <td className="up-cell-email">{u.email || <span className="up-muted">-</span>}</td>
                <td>{u.customerEmployeeId || <span className="up-muted">-</span>}</td>
                <td>{rolesText(u) || <span className="up-muted">-</span>}</td>
                <td>
                  {u.teamName
                    ? <span className="up-team-pill">{u.teamName}</span>
                    : <span className="up-muted">-</span>}
                </td>
                <td>{renderSites(u)}</td>
                <td>{u.assignedDeviceId || <span className="up-muted">-</span>}</td>
                <td>
                  <span className={`up-status-pill up-status-${status.toLowerCase()}`}>
                    <span className={`up-dot up-dot-${status === 'ACTIVE' ? 'active' : 'inactive'}`} />
                    {status === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit user"
                    aria-label="Edit user"
                    onClick={() => handleEdit(u)}
                    disabled={deletingId === u.employeeId}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Delete user"
                    aria-label="Delete user"
                    onClick={() => handleDelete(u)}
                    disabled={deletingId === u.employeeId}
                  >
                    <i className={`fas ${deletingId === u.employeeId ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <UserDrawer
        open={showAddUser || !!editingUser}
        user={editingUser}
        onClose={() => { setShowAddUser(false); setEditingUser(null); }}
        onSaved={() => { refreshList(); }}
      />
      <CsvImportModal
        open={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        onSaved={() => { refreshList(); }}
      />
    </>
  );
}
